import { DictTypes } from '@/constants';
import {
  type DictExtrasTypes,
  type GetDictContentType,
  type TConfirmedDict,
  type TDict
} from '@/types/dicts';

// Определение связанных справочников
const dependedDicts: Record<DictExtrasTypes, DictTypes[]> = {
  [DictTypes.Dealers]: [DictTypes.DealersBrands, DictTypes.DealersUsedDealers],
  [DictTypes.SupportCategories]: [DictTypes.ModulesSupportCategories]
};
export const DEPENDED_DICTS_LIST = Object.entries(dependedDicts) as [
  keyof typeof dependedDicts,
  DictTypes[]
][];

// Справочники, которые имеют определенные запросы (не bulk)
// P.s. После изменения списка, нужно обновить файл src/store/dict.slice.ts
export const SPECIFIC_FETCH_DICT_TYPES = [
  DictTypes.Cities,
  DictTypes.DealersCities
] as const;

export const PRELOAD_DICT_TYPES: DictTypes[] = Object.values(DictTypes);

const isValidKey = (key: any): key is string | number => {
  return typeof key === 'string' ? key.length > 0 : typeof key === 'number';
};

export const safeKey = (key: any): string => {
  if (isValidKey(key)) return String(key);
  return '';
};

export const isDictHasAll = <T extends DictTypes>(
  dicts: TDict | null,
  dictTypes: T[]
): dicts is TConfirmedDict<T> => {
  if (!dicts) return false;
  return dictTypes.every((dictType) => !!dicts[dictType]);
};

export const mapContentItemsToGetBy = <T extends DictTypes>(
  targetDict: TDict[T]
) => {
  if (!targetDict) return;
  for (const item of targetDict.content) {
    if (isValidKey(item.uuid)) {
      targetDict.getBy.uuid[safeKey(item.uuid)] = item;
    }
    if ('code' in item && isValidKey(item.code)) {
      targetDict.getBy.code[safeKey(item.code)] = item;
    }
    if ('name' in item && isValidKey(item.name)) {
      targetDict.getBy.name[safeKey(item.name)] = item;
    }
  }
};

export const fillDictExtras = (
  dictType: DictExtrasTypes,
  dicts: Partial<TDict>
) => {
  (
    ({
      [DictTypes.Dealers]: () => {
        const dealers = dicts[DictTypes.Dealers];
        const dealersBrands = dicts[DictTypes.DealersBrands];
        const dealersUsedDealers = dicts[DictTypes.DealersUsedDealers];

        if (!dealers || !dealersBrands || !dealersUsedDealers) return;

        // Заполнение brandUuids
        dealersBrands.content.forEach((item) => {
          const dealer = dealers.getBy.uuid[safeKey(item.dealer_uuid)];
          if (dealer) {
            dealer._brandUuids.push(item.brand_uuid);
          }
        });
        dealers.content.forEach((item) => {
          if (
            item.sold_brands &&
            !item._brandUuids.includes(item.sold_brands)
          ) {
            item._brandUuids.push(item.sold_brands);
          }
        });

        // Заполнение dealerUuids
        dealersUsedDealers.content.forEach((item) => {
          const dealer = dealers.getBy.uuid[safeKey(item.dealer_uuid)];
          if (dealer) {
            dealer._dealerUuids.push(item.used_dealer_uuid);
          }
        });
      },
      [DictTypes.SupportCategories]: () => {
        const supportCategories = dicts[DictTypes.SupportCategories];
        const modulesSupportCategories =
          dicts[DictTypes.ModulesSupportCategories];

        if (!supportCategories || !modulesSupportCategories) return;

        // Заполнение moduleUuids
        modulesSupportCategories.content.forEach((item) => {
          const supportCategory =
            supportCategories.getBy.uuid[safeKey(item.support_category_uuid)];
          if (supportCategory) {
            supportCategory._moduleUuids.push(item.module_uuid);
          }
        });
      }
    }) satisfies Record<DictExtrasTypes, () => void>
  )[dictType]();
};

export const dictContentMapper = (
  dictType: DictTypes,
  dictContentMap: Map<DictTypes, GetDictContentType<DictTypes>[]>,
  dicts: Partial<TDict>
) => {
  if (!dicts[dictType]) {
    dicts[dictType] = {
      content: [],
      getBy: {
        uuid: {},
        name: {},
        code: {}
      }
    };
  }

  const getContent = <T extends DictTypes>(
    dictType: T
  ): GetDictContentType<T>[] => {
    return (dictContentMap.get(dictType) ?? []) as GetDictContentType<T>[];
  };

  (
    ({
      [DictTypes.AddContactsIsRelated]: () => {
        const targetDict = dicts[DictTypes.AddContactsIsRelated];
        if (targetDict)
          targetDict.content = getContent(DictTypes.AddContactsIsRelated).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.AutoTypes]: () => {
        const targetDict = dicts[DictTypes.AutoTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.AutoTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Banks]: () => {
        const targetDict = dicts[DictTypes.Banks];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Banks).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Brands]: () => {
        const targetDict = dicts[DictTypes.Brands];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Brands).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Cities]: () => {
        const targetDict = dicts[DictTypes.Cities];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Cities).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Colors]: () => {
        const targetDict = dicts[DictTypes.Colors];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Colors).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.CompanyActivityTypes]: () => {
        const targetDict = dicts[DictTypes.CompanyActivityTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.CompanyActivityTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Countries]: () => {
        const targetDict = dicts[DictTypes.Countries];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Countries).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Dealers]: () => {
        const targetDict = dicts[DictTypes.Dealers];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Dealers)
            .map((item) => ({
              ...item,
              _brandUuids: [],
              _dealerUuids: []
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.DealersBrands]: () => {
        const targetDict = dicts[DictTypes.DealersBrands];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DealersBrands);
      },
      [DictTypes.DealersCities]: () => {
        const targetDict = dicts[DictTypes.DealersCities];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DealersCities).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.DealersTypes]: () => {
        const targetDict = dicts[DictTypes.DealersTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DealersTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.DealersUsedDealers]: () => {
        const targetDict = dicts[DictTypes.DealersUsedDealers];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DealersUsedDealers);
      },
      [DictTypes.DocumentIssuringAuthorities]: () => {
        const targetDict = dicts[DictTypes.DocumentIssuringAuthorities];
        if (targetDict)
          targetDict.content = getContent(
            DictTypes.DocumentIssuringAuthorities
          ).sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.DocumentTypes]: () => {
        const targetDict = dicts[DictTypes.DocumentTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DocumentTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.DriveTypes]: () => {
        const targetDict = dicts[DictTypes.DriveTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DriveTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.EducationTypes]: () => {
        const targetDict = dicts[DictTypes.EducationTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.EducationTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.EngineTypes]: () => {
        const targetDict = dicts[DictTypes.EngineTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.EngineTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Equipments]: () => {
        const targetDict = dicts[DictTypes.Equipments];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Equipments).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.FaqPagesTypes]: () => {
        const targetDict = dicts[DictTypes.FaqPagesTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.FaqPagesTypes);
      },
      [DictTypes.GearboxTypes]: () => {
        const targetDict = dicts[DictTypes.GearboxTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.GearboxTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Genders]: () => {
        const targetDict = dicts[DictTypes.Genders];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Genders).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.InsuranceInstallmentPeriods]: () => {
        const targetDict = dicts[DictTypes.InsuranceInstallmentPeriods];
        if (targetDict)
          targetDict.content = getContent(
            DictTypes.InsuranceInstallmentPeriods
          ).sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.InsuranceInstallmentTypes]: () => {
        const targetDict = dicts[DictTypes.InsuranceInstallmentTypes];
        if (targetDict)
          targetDict.content = getContent(
            DictTypes.InsuranceInstallmentTypes
          ).sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.InsurancePaymentTypes]: () => {
        const targetDict = dicts[DictTypes.InsurancePaymentTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.InsurancePaymentTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Insurances]: () => {
        const targetDict = dicts[DictTypes.Insurances];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Insurances).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.IntegrationTypes]: () => {
        const targetDict = dicts[DictTypes.IntegrationTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.IntegrationTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.LeasingCompanies]: () => {
        const targetDict = dicts[DictTypes.LeasingCompanies];
        if (targetDict)
          targetDict.content = getContent(DictTypes.LeasingCompanies).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.LeasingIndividualsSettings]: () => {
        const targetDict = dicts[DictTypes.LeasingIndividualsSettings];
        if (targetDict)
          targetDict.content = getContent(DictTypes.LeasingIndividualsSettings);
      },
      [DictTypes.LoanTerms]: () => {
        const targetDict = dicts[DictTypes.LoanTerms];
        if (targetDict)
          targetDict.content = getContent(DictTypes.LoanTerms).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.MainActivities]: () => {
        const targetDict = dicts[DictTypes.MainActivities];
        if (targetDict)
          targetDict.content = getContent(DictTypes.MainActivities).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.MaritalStatuses]: () => {
        const targetDict = dicts[DictTypes.MaritalStatuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.MaritalStatuses).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Models]: () => {
        const targetDict = dicts[DictTypes.Models];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Models).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Modules]: () => {
        const targetDict = dicts[DictTypes.Modules];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Modules).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.ModulesSupportCategories]: () => {
        const targetDict = dicts[DictTypes.ModulesSupportCategories];
        if (targetDict)
          targetDict.content = getContent(DictTypes.ModulesSupportCategories);
      },
      [DictTypes.OnlineRequestFcStatuses]: () => {
        const targetDict = dicts[DictTypes.OnlineRequestFcStatuses];
        if (targetDict)
          targetDict.content = getContent(
            DictTypes.OnlineRequestFcStatuses
          ).sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.OrganizationTypes]: () => {
        const targetDict = dicts[DictTypes.OrganizationTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.OrganizationTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.PaymentDates]: () => {
        const targetDict = dicts[DictTypes.PaymentDates];
        if (targetDict)
          targetDict.content = getContent(DictTypes.PaymentDates).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.PoscredBankProducts]: () => {
        const targetDict = dicts[DictTypes.PoscredBankProducts];
        if (targetDict)
          targetDict.content = getContent(DictTypes.PoscredBankProducts).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.PoscredProducts]: () => {
        const targetDict = dicts[DictTypes.PoscredProducts];
        if (targetDict)
          targetDict.content = getContent(DictTypes.PoscredProducts).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Positions]: () => {
        const targetDict = dicts[DictTypes.Positions];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Positions).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Products]: () => {
        const targetDict = dicts[DictTypes.Products];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Products).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.ProductsTypes]: () => {
        const targetDict = dicts[DictTypes.ProductsTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.ProductsTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.PropertyTypes]: () => {
        const targetDict = dicts[DictTypes.PropertyTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.PropertyTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.RepaymentTypes]: () => {
        const targetDict = dicts[DictTypes.RepaymentTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.RepaymentTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.ResidentStatuses]: () => {
        const targetDict = dicts[DictTypes.ResidentStatuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.ResidentStatuses).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.SalePoints]: () => {
        const targetDict = dicts[DictTypes.SalePoints];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SalePoints).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.SocialStatuses]: () => {
        const targetDict = dicts[DictTypes.SocialStatuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SocialStatuses).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Statuses]: () => {
        const targetDict = dicts[DictTypes.Statuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Statuses).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.SupportCategories]: () => {
        const targetDict = dicts[DictTypes.SupportCategories];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SupportCategories)
            .map((item) => ({ ...item, _moduleUuids: [] }))
            .sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.SupportReasons]: () => {
        const targetDict = dicts[DictTypes.SupportReasons];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SupportReasons).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.SupportTemplates]: () => {
        const targetDict = dicts[DictTypes.SupportTemplates];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SupportTemplates).map(
            (item) => ({ ...item, _moduleUuids: [] })
          );
      },
      [DictTypes.VehicleRegistrationCompanies]: () => {
        const targetDict = dicts[DictTypes.VehicleRegistrationCompanies];
        if (targetDict)
          targetDict.content = getContent(
            DictTypes.VehicleRegistrationCompanies
          ).sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.Years]: () => {
        const targetDict = dicts[DictTypes.Years];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Years).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      }
    }) satisfies Record<DictTypes, () => void>
  )[dictType]();
};
